import type { ValueOf } from '@evc/web-components';

/** This environment will drive which api to use
 * - connect apis may be consumed for auth, fetching base user/organization data, licences, permission...
 * Expect to consume dev/stage/prod connect api according to those (local/testing use dev connect apis)
 */
export enum ENVIRONMENT {
  /** use connect api in local mode */
  connect_local = 'connect_local',
  connect_testing = 'connect_testing',
  /** use connect api in local `dev` mode - allowing you to still define your local api in your local environment file */
  local = 'local',
  testing = 'testing',
  development = 'development',
  staging = 'staging',
  production = 'production',
}
export type Env = `${ENVIRONMENT}`;

export type AvailableLanguages = 'en' | 'fr';
export type I18nEnv = {
  LANG?: AvailableLanguages | string;
  LOCALES?: (AvailableLanguages | string)[];
  LOCALES_STORAGE_KEY?: string;
};

/** We may attempt to retrieve current auth on init:
 * - if on a private route
 * - if an account is found in storage */
export const AUTH_INITIALIZATION_STRATEGIES = {
  /** Default: enforce fetching a new token.
   * - Ensures the user is authenticated in their last connected organization
   * - Note: this will trigger an HTTP call */
  FORCE_REFRESH: 'force-refresh',
  /** Silent attempt: only checks current storage */
  SILENT: 'silent',
  /** No check: use guard to trigger auth or handle manually */
  NONE: 'none',
} as const;
export type AuthInitializationStrategy = ValueOf<typeof AUTH_INITIALIZATION_STRATEGIES>;

/** https://learn.microsoft.com/en-us/entra/identity-platform/msal-js-prompt-behavior */
export const AUTH_LOGIN_DEFAULT_PROMPTS = {
  LOGIN: 'login',
  NONE: 'none',
  CONSENT: 'consent',
  SELECT_ACCOUNT: 'select_account',
  CREATE: 'create',
} as const;
export type AuthDefaultPrompt = ValueOf<typeof AUTH_LOGIN_DEFAULT_PROMPTS>;

export type AuthEnv = {
  /** Enabled by default if USE_CONNECT_PLATFORM and AUTH_CLIENT_ID are set */
  AUTH_ENABLED?: true | boolean;
  /** Register your IDP application via IaC */
  AUTH_CLIENT_ID?: string;
  /** Strategy to use to get token on initialization */
  AUTH_INITIALIZATION_STRATEGY?: 'force-refresh' | AuthInitializationStrategy;
  /** Init flow may redirect you to create an organization if the user has none */
  AUTH_FORCE_ORGANIZATION?: false | boolean;
  /** Init flow may redirect you to homepage (with toast message) if the user has no license */
  AUTH_FORCE_LICENSE?: false | boolean;
  /** If defined, will be used by default for login requests (AuthService and MSALGuard) */
  AUTH_DEFAULT_PROMPT?: undefined | AuthDefaultPrompt;
  /** May allow auth to connect to the last found session—only on reload, not navigation */
  AUTH_ENABLE_SESSION_ON_RELOAD?: false | boolean;
  /** Special route that implements our AuthFailureGuard */
  AUTH_FAIL_ROUTE?: 'auth-fail' | string;
  /** May disable captcha during auth (for testing) */
  AUTH_CAPTCHA_BYPASS_TOKEN?: string;
  /** Activate logs for auth-service */
  DEBUG_AUTH_FLOW?: false | boolean;
  /** You may disable our custom auth domain (login.connect) - so will use https://<tenant>>.b2clogin.com/<tenant>.onmicrosoft.com */
  AUTH_IGNORE_AUTHORITY_DOMAIN?: false | boolean;
  /**
   * DEACTIVATED - KEEP IT FOR DOMAIN MIGRATIONS
   *
   * Use legacy domain for auth requests
   * - if falsy will use login.connect.dev-evcnt.com)
   * - TEMPORARY fix (for localhost only !) - other environments will automatically detect with location.hostname
   */
  AUTH_USE_LEGACY_DOMAIN?: false; // | boolean
  /** Prompt user to confirm/select their organization after login, before showing app content */
  AUTH_PROMPT_ORGANIZATIONS?: false | boolean;
};

export type ApiEnv = {
  /** Stores key-value pairs of protected URIS (CORE, DEVICE, DEMOAPP, DRIVE, etc...) */
  PROTECTED_URIS?: Record<string, string>;

  /** merge user profil with a mock */
  DEBUG_MOCK_USER?: false | boolean;
  /** mock get linked organizations */
  DEBUG_MOCK_LINKED_ORGANIZATIONS?: boolean;
};

export type AppIdentityEnv = {
  APP_NAME?: string;
  APP_TITLE?: string;
  APP_LOGO?: string;
};

export type PlatformEnvConnect = {
  USE_CONNECT_PLATFORM?: boolean;
  /** enable logs in platform loading flow - usefull to investigate when other teams stuck in loader */
  DEBUG_PLATFORM?: false | boolean;
  /** enable debugs for licencing (user and admin) */
  DEBUG_LICENSING?: false | boolean;
} & ApiEnv &
  AuthEnv;

export type PlatformEnv = {
  ENV: Env;
  PORT?: number | string;
} & AppIdentityEnv &
  I18nEnv &
  PlatformEnvConnect;
